import React, { useEffect, useState, useContext } from "react";
import axios from "../context/AxiosInstance";
import { Button, Form, Spinner } from "react-bootstrap";
import {
  FaUsers, FaUserCheck, FaUserSlash, FaKey, FaUserPlus,
  FaEdit, FaTrashAlt, FaSearch, FaToggleOn, FaToggleOff, FaBell,
} from "react-icons/fa";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import { AuthContext } from "../context/AuthContext";
import Navbar from "../components/Navbar";
import AppBreadcrumb from "../components/AppBreadcrumb";
import Sidebar from "../components/Sidebar";
import UtilisateurFormModal from "../components/utilisateur/UtilisateurFormModal";
import ConfirmDeleteModal from "../components/utilisateur/ConfirmDeleteModal";
import EmptyState from "../components/common/EmptyState";
import RhTablePagination from "../components/common/RhTablePagination";
import "./UtilisateursPage.css";

const AVATAR_GRADIENTS = {
  ADMIN: "linear-gradient(135deg,#7c3aed,#a855f7)",
  RH: "linear-gradient(135deg,#2563eb,#60a5fa)",
  ASSISTANT: "linear-gradient(135deg,#0d9488,#2dd4bf)",
  PERSONNEL: "linear-gradient(135deg,#64748b,#94a3b8)",
};

const initials = (nom = "", prenom = "") =>
  `${(nom[0] || "").toUpperCase()}${(prenom[0] || "").toUpperCase()}` || "?";

const UtilisateursPage = () => {
  const { token, role } = useContext(AuthContext);
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [loading, setLoading] = useState(true);

  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(10);

  const [showForm, setShowForm] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showDelete, setShowDelete] = useState(false);

  const isAdmin = role === "ADMIN";

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const res = await axios.get("/utilisateurs/", {
        headers: { Authorization: `Bearer ${token}` },
      });
      setUsers(Array.isArray(res.data?.data) ? res.data.data : []);
    } catch (err) {
      console.error("Erreur chargement utilisateurs", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token && isAdmin) fetchUsers();
    // eslint-disable-next-line
  }, [token, role]);

  const openForm = (u = null) => {
    setSelectedUser(u);
    setIsEdit(!!u);
    setShowForm(true);
  };

  const openDelete = (u) => {
    setSelectedUser(u);
    setShowDelete(true);
  };

  const toggleActivation = async (utilisateur) => {
    const newState = !utilisateur.is_active;
    try {
      await axios.patch(
        `/utilisateurs/${utilisateur.id_user}/etat`,
        { is_active: newState },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`✅ Compte ${newState ? "activé" : "désactivé"} avec succès`);
      fetchUsers();
    } catch (err) {
      toast.error("❌ Échec de l'activation/désactivation");
    }
  };

  const resetPassword = async (u) => {
    const c = await Swal.fire({
      title: "Réinitialiser le mot de passe",
      html: `Générer un nouveau mot de passe par défaut pour <b>${u.nom} ${u.prenom}</b> (login : <b>${u.login}</b>) ?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#6366f1",
      confirmButtonText: "Oui, réinitialiser",
      cancelButtonText: "Annuler",
    });
    if (!c.isConfirmed) return;
    try {
      const res = await axios.post(
        `/utilisateurs/${u.id_user}/reinitialiser-mot-de-passe`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const pwd = res.data?.data?.nouveau_mot_de_passe || "";
      await Swal.fire({
        title: "Mot de passe réinitialisé ✅",
        html: `Nouveau mot de passe pour <b>${u.login}</b> :<br/><br/>`
          + `<code style="font-size:1.4rem;background:#f1f1f1;padding:6px 12px;border-radius:6px">${pwd}</code>`
          + `<br/><br/>Communiquez-le à l'utilisateur. Il pourra se connecter avec ce mot de passe.`,
        icon: "success",
      });
      fetchUsers();
    } catch (err) {
      Swal.fire("Erreur ❌", err.response?.data?.detail?.message || "Échec de la réinitialisation", "error");
    }
  };

  const pendingResets = users.filter((u) => u.mot_de_passe_oublie);
  const nbActifs = users.filter((u) => u.is_active).length;
  const nbInactifs = users.length - nbActifs;

  const filtered = users.filter((u) => {
    const matchText = `${u.nom} ${u.prenom} ${u.login}`.toLowerCase().includes(search.toLowerCase());
    const matchRole = !roleFilter || u.type_compte === roleFilter;
    return matchText && matchRole;
  });

  const currentData = filtered.slice((currentPage - 1) * perPage, currentPage * perPage);

  const StatCard = ({ icon, klass, value, label }) => (
    <div className="ustat-card">
      <div className={`ustat-ic ${klass}`}>{icon}</div>
      <div>
        <div className="ustat-val">{value}</div>
        <div className="ustat-lbl">{label}</div>
      </div>
    </div>
  );

  return (
    <div className="d-flex users-page">
      <Sidebar />
      <div className="main-content">
        <Navbar />
        <div className="container-fluid p-4 p-lg-5">
          <AppBreadcrumb items={[{ label: "Accueil", to: "/" }, { label: "Utilisateurs" }]} />

          {!isAdmin ? (
            <div className="u-restricted mt-3">
              <h5 className="fw-bold mb-2">🔒 Accès restreint</h5>
              Seuls les administrateurs peuvent accéder à la gestion des utilisateurs.
            </div>
          ) : (
            <>
              {/* Hero */}
              <div className="users-hero">
                <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
                  <div>
                    <h1 className="hero-title">
                      <span className="hero-icon"><FaUsers /></span>
                      Gestion des utilisateurs
                    </h1>
                    <div className="hero-sub">Créez, activez et sécurisez les comptes de l'application.</div>
                  </div>
                  <Button className="btn-hero-add" onClick={() => openForm(null)}>
                    <FaUserPlus className="me-2" /> Ajouter un utilisateur
                  </Button>
                </div>
              </div>

              {loading ? (
                <div className="text-center my-5">
                  <Spinner animation="border" variant="primary" />
                  <p className="text-muted mt-2">Chargement des utilisateurs...</p>
                </div>
              ) : (
                <>
                  {/* Stats */}
                  <div className="users-stats">
                    <StatCard icon={<FaUsers />} klass="ic-total" value={users.length} label="Utilisateurs au total" />
                    <StatCard icon={<FaUserCheck />} klass="ic-active" value={nbActifs} label="Comptes actifs" />
                    <StatCard icon={<FaUserSlash />} klass="ic-inactive" value={nbInactifs} label="Comptes inactifs" />
                    <StatCard icon={<FaKey />} klass="ic-reset" value={pendingResets.length} label="Demandes de réinit." />
                  </div>

                  {/* Bannière demandes reset */}
                  {pendingResets.length > 0 && (
                    <div className="reset-banner">
                      <div className="rb-title"><FaBell /> {pendingResets.length} demande(s) de mot de passe oublié</div>
                      <div className="d-flex flex-wrap gap-2 mt-2">
                        {pendingResets.map((u) => (
                          <Button key={u.id_user} size="sm" className="reset-chip" onClick={() => resetPassword(u)}>
                            {u.nom} {u.prenom} ({u.login}) — Réinitialiser
                          </Button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Toolbar */}
                  <div className="users-toolbar d-flex justify-content-between align-items-center flex-wrap gap-3">
                    <div className="d-flex align-items-center gap-3 flex-grow-1 flex-wrap">
                      <div className="u-search">
                        <FaSearch className="u-search-ic" />
                        <Form.Control
                          type="text"
                          placeholder="Rechercher nom, prénom ou login..."
                          value={search}
                          onChange={(e) => { setSearch(e.target.value); setCurrentPage(1); }}
                        />
                      </div>
                      <Form.Group className="u-rolefilter mb-0" style={{ minWidth: 170 }}>
                        <Form.Select value={roleFilter} onChange={(e) => { setRoleFilter(e.target.value); setCurrentPage(1); }}>
                          <option value="">Tous les rôles</option>
                          <option value="ADMIN">Admin</option>
                          <option value="RH">RH</option>
                          <option value="ASSISTANT">Assistant</option>
                          <option value="PERSONNEL">Personnel</option>
                        </Form.Select>
                      </Form.Group>
                    </div>
                    <span className="text-muted small">{filtered.length} résultat(s)</span>
                  </div>

                  {/* Tableau */}
                  {currentData.length > 0 ? (
                    <div className="users-table-card">
                      <div className="table-responsive">
                        <table className="table users-table">
                          <thead>
                            <tr>
                              <th>Utilisateur</th>
                              <th>Rôle</th>
                              <th>Téléphone</th>
                              <th>Statut</th>
                              <th>Créé le</th>
                              <th className="text-end">Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {currentData.map((u) => (
                              <tr key={u.id_user}>
                                <td>
                                  <div className="u-userbox">
                                    <div className="uavatar" style={{ background: AVATAR_GRADIENTS[u.type_compte] || AVATAR_GRADIENTS.PERSONNEL }}>
                                      {initials(u.nom, u.prenom)}
                                    </div>
                                    <div>
                                      <div className="u-uname">{u.nom} {u.prenom}</div>
                                      <div className="u-ulogin">@{u.login}</div>
                                    </div>
                                  </div>
                                </td>
                                <td><span className={`role-chip role-${u.type_compte}`}>{u.type_compte}</span></td>
                                <td>{u.telephone || "—"}</td>
                                <td>
                                  {u.is_active ? (
                                    <span className="status-pill status-on"><span className="status-dot" />Actif</span>
                                  ) : (
                                    <span className="status-pill status-off"><span className="status-dot" />Inactif</span>
                                  )}
                                  {u.mot_de_passe_oublie && <span className="mdp-pill"><FaKey size={10} /> MDP oublié</span>}
                                </td>
                                <td className="text-muted small">{u.date_creation ? new Date(u.date_creation).toLocaleDateString("fr-FR") : "—"}</td>
                                <td>
                                  <div className="u-actions">
                                    <button className="uact edit" title="Modifier" onClick={() => openForm(u)}><FaEdit /></button>
                                    <button
                                      className={`uact toggle ${u.is_active ? "on" : ""}`}
                                      title={u.is_active ? "Désactiver" : "Activer"}
                                      onClick={() => toggleActivation(u)}
                                    >
                                      {u.is_active ? <FaToggleOn /> : <FaToggleOff />}
                                    </button>
                                    <button
                                      className={`uact key ${u.mot_de_passe_oublie ? "alert" : ""}`}
                                      title="Réinitialiser le mot de passe"
                                      onClick={() => resetPassword(u)}
                                    >
                                      <FaKey />
                                    </button>
                                    <button className="uact del" title="Supprimer" onClick={() => openDelete(u)}><FaTrashAlt /></button>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  ) : (
                    <EmptyState title="Aucun utilisateur" description="Créez un utilisateur ou modifiez les filtres de recherche." />
                  )}

                  {currentData.length > 0 && (
                    <RhTablePagination
                      currentPage={currentPage}
                      totalItems={filtered.length}
                      perPage={perPage}
                      onPageChange={setCurrentPage}
                      onPerPageChange={(n) => { setPerPage(n); setCurrentPage(1); }}
                      itemLabel="utilisateurs"
                    />
                  )}
                </>
              )}
            </>
          )}
        </div>

        {isAdmin && (
          <>
            <UtilisateurFormModal
              show={showForm}
              handleClose={() => setShowForm(false)}
              isEditMode={isEdit}
              selectedUser={selectedUser}
              token={token}
              refreshData={fetchUsers}
            />
            <ConfirmDeleteModal
              show={showDelete}
              handleClose={() => setShowDelete(false)}
              selectedItem={selectedUser}
              token={token}
              refreshData={fetchUsers}
            />
          </>
        )}
      </div>
    </div>
  );
};

export default UtilisateursPage;
